import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import {
  ButtonView,
  ImageScaleView,
  ImageScaleViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { downscale } from 'src/conf/scale';
import { SuperLollipopAnimation } from '../components/SuperLollipopAnimation';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import sounds from 'src/lib/sounds';
import StateObserver from 'src/StateObserver';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import i18n from 'src/lib/i18n/i18n';

type CloseHandler = (ok: boolean) => void;

export type PopupSuperLollipopActiveMode = 'tapToPlay' | 'tapToContinue';
export default class PopupSuperLollipopActive extends PopupBasicLazy {
  private mode: PopupSuperLollipopActiveMode = 'tapToPlay';
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.close = opts.close;
    this.buttonClose.hide();

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    // This popup should close on any tap
    this.box.updateOpts({
      clip: true,
    });

    // Screen tap area
    new ButtonView({
      superview: this.box,
      width: width,
      height: height,
      zIndex: 1,
      onClick: () => {
        sounds.sfx.play('ok');
        opts.close(false);
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/superLollipop/shared']),
      ...AssetGroup.constructURLs(['assets/ui/superLollipop/popupActive']),
      {
        loadMethod: spineLoader,
        url: 'assets/ui/superLollipop/animation/shine-and-confetti',
      },
    ]);
  }

  public async init(args: { mode?: PopupSuperLollipopActiveMode }) {
    super.init({});
    if (args.mode) {
      this.mode = args.mode;
    }
  }

  public async fadeIn() {
    await super.fadeIn();
    this.animateIn();
  }

  private async animateIn() {
    await waitForItPromise(300);

    const locale = StateObserver.getLocale();
    const en = locale === 'en';

    sounds.sfx.play('tada');

    const title = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/superLollipop/shared/title_overlay_${locale}.png`,
      width: downscale(en ? 591 : 508),
      height: downscale(en ? 98 : 92),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 300,
      centerOnOrigin: true,
    });
    await this.showAnimated(title);

    const active = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/superLollipop/popupActive/active_${locale}.png`,
      width: downscale(en ? 713 : 510),
      height: downscale(en ? 66 : 63),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: title.style.y + 60,
      centerOnOrigin: true,
    });
    await this.showAnimated(active);

    sounds.sfx.play('puzzle-win-confetti');
    const animation = new SuperLollipopAnimation({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: this.box.style.height * 0.5,
    });
    await this.showAnimated(animation);

    sounds.sfx.play('applause');
    this.playConfetti();

    const modeMethods = {
      tapToContinue: () => this.showAsTapToContinue(),
      tapToPlay: () => this.showAsTapToPlay(),
    };

    await modeMethods[this.mode]();
  }

  private async showAsTapToContinue() {
    const locale = StateObserver.getLocale();
    const en = locale === 'en';
    const cta = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/superLollipop/shared/tap_to_continue_${locale}.png`,
      width: downscale(en ? 569 : 433),
      height: downscale(en ? 87 : 84),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 980,
      centerOnOrigin: true,
    });
    await this.showAnimated(cta);
  }

  private async showAsTapToPlay() {
    const cta = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      x: this.box.style.width * 0.5,
      y: 980,
      fontSize: 36,
      labelOffsetY: -2,
      font: 'Body',
      width: 300,
      height: 90,
      centerOnOrigin: true,
      zIndex: 2, // Just above the screen tap area
      localeText: () => i18n('superLollipop.popupActive.ctaPlay'),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.close(true);
      },
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.POPUP_ACTIVE_CTA,
      },
    });
    await this.showAnimated(cta);
  }

  private async playConfetti() {
    const spinePath = 'assets/ui/superLollipop/animation/shine-and-confetti';
    await spineLoader(spinePath);
    const spine = new SpineView({
      superview: this.box,
      spinePath,
      zIndex: -1,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2 + 100,
      scale: 0.5,
    });
    spine.state.setAnimation(0, 'confetti', true);
    // fade out just before complete to hide an animation glitch
    waitForItPromise(1700).then(() => {
      animate(spine).now({ opacity: 0 }, 200, animate.linear);
    });
  }

  private async showAnimated(view: View) {
    view.updateOpts({ scale: 0 });
    animate(view).then({ scale: 1 }, 300, animate.easeOutBack);
    sounds.sfx.play('puzzle-ice-tap');
    await waitForItPromise(200);
  }
}
